import type { Meta, StoryObj } from '@storybook/vue3';
// @ts-ignore
import Calculator from './Calculator.vue';
import { within, userEvent } from '@storybook/test';

const meta = {
  title: 'Organisms/Calculator',
  tags: ['autodocs'],
  component: Calculator,
  render: (args: any) => ({
    components: { Calculator },
    setup() {
      return { args };
    },
    template:
      '<div class="bg-grey" style="padding:50px"><Calculator v-bind="args" /></div>',
  }),
  argTypes: {
    width: {
      name: 'View Type',
      type: 'select',
      defaultValue: 'half',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'half' },
      },
      options: ['half', 'full'],
    },
    customerOptions: {
      type: 'select',
      name: 'Customer Type',
      options: ['b2c', 'b2b', 'b2c_b2b'],
    },
    theme: {
      name: 'Theme',
      type: 'select',
      options: ['dark', 'light', 'grey'],
    },
    componentId: {
      table: {
        disable: true,
      },
    },
    productTypes: {
      table: {
        disable: true,
      },
    },
    promotionCode: {
      table: {
        disable: true,
      },
    },
    disableVouchers: {
      table: {
        disable: true,
      },
    },
    onlyThisPage: {
      table: {
        disable: true,
      },
    },
    borderColor: {
      type: 'select',
      name: 'Border Color',
      options: ['none', 'black', 'white', 'grey'],
    },
  },
  args: {
    disableVouchers: '1',
    promotionCode: '0',
    onlyThisPage: '0',
    borderColor: 'none',
  },
} satisfies Meta<typeof Calculator>;

export default meta;
type Story = StoryObj<typeof meta>;

export const HalfView: Story = {
  ...meta,
  args: {
    ...meta.args,
    width: 'half',
    theme: 'dark',
    customerOptions: 'b2c_b2b',
    componentId: '123',

    parameters: JSON.stringify({
      customer: '01',
      postcode: '31785',
      city: 'Hameln',
      cscData: {
        customer: {
          gender: null,
          honorificPrefix: null,
          givenName: 'userSecond',
          familyName: 'user2',
          birthDate: '07.08.1948',
          email: 'd.udrea@house-of-communication.com',
          telephone: '495151787280',
          billingAddress: [],
          deliveryAddress: {
            postalCode: '31785',
            addressLocality: 'Hameln',
            streetAddress: {
              name: 'Bennigsenstr.',
              number: '10',
              numberAddition: '',
            },
          },
        },
        paymentMethod: 'BANK_COLLECTION',
        backURL: 'https://mein-test.e-wie-einfach.de/checkout',
        mainSection: '',
      },
      productrange: 'pes',
      calculator: '01',
      slider_usage_counter: null,
      slider_usage_counter_gk: null,
      input_field_used: null,
      household_icon: null,
      house_m2_icon: null,
      house_m2: null,
      industry_icon: null,
      recalculated: null,
      voucher_code: null,
      consumption: '2500',
      pricing: null,
      promotion_code: '0',
      agentCode: 0,
      reseller: 50,
      ratesOverviewPage:
        '/mobileapp/preisberechnung?calculator=01\u0026customer=01\u0026disableVouchers=1\u0026promotion_code=0\u0026onlyThisPage=0\u0026postcode=31785\u0026city=Hameln\u0026consumption=2500',
    }),

    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
  },
};

// ... Rest of the stories following the same pattern ...
export const FullView: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const GreyTheme: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'grey',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const LightTheme: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'light',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const FourProducts: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: false,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: true,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><strong>350€</strong>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
      '05': {
        type: '05',
        typeReadable: 'dynamic_strom',
        isSelected: false,
        redirectPage: 'strom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const DynamicStromTeaser: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: false,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><strong>350€</strong>Bonus',
      },
      '05': {
        type: '05',
        typeReadable: 'dynamic_strom',
        isSelected: true,
        showDynamicEnergyTeaser: true,
        dynamicEnergyRedirectPage:
          'https://www2.e-wie-einfach.de/einfachdynamisch/',
        dynamicEnergyTeaserText:
          '<p>Du fährst ein E-Auto oder Plug-in-Hybrid? Dann profitiere jetzt von einem dynamischen Stromtarif.</p><ul><li>Laden, wenn der Strom günstig ist</li><li>Du hast die Kontrolle mit unserer App</li><li>Dein Arbeitspreis ist so flexibel wie du</li></ul>',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const DynamicStromCalculator: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: false,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
      '05': {
        type: '05',
        typeReadable: 'dynamic_strom',
        isSelected: true,
        showDynamicEnergyTeaser: false,
        redirectPage: 'dynamischer-strom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const SingleProduct: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const SinglePowerReduced: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    reduced: '1',
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><strong>300€</strong>Bonus',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const SingleGasReduced: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    reduced: '1',
    productTypes: {
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: true,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><strong>350€</strong>Bonus',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const SingleHeatflowReduced: Story = {
  ...meta,
  args: {
    width: 'full',
    theme: 'dark',
    reduced: '1',
    productTypes: {
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: true,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
    customerOptions: 'b2c_b2b',
  },
};

export const InteractivePower: Story = {
  ...FullView,
  args: {
    ...FullView.args,
  },
  play: async ({ canvasElement }: { canvasElement: HTMLElement }) => {
    const canvas = within(canvasElement);
    const powerTabButton = canvas.getByRole('tab', { name: 'Strom' });
    await userEvent.click(powerTabButton);
    const radioBusinessButton = canvas.getByRole('radio', {
      name: 'Geschäftskunden',
    });
    await userEvent.click(radioBusinessButton);

    const zipInput = canvas.getByTestId('zip-single-option');
    await userEvent.click(zipInput);
    await userEvent.type(zipInput, '12345', {
      delay: 100,
    });

    const sliderInput = canvas.getByRole('slider');

    await userEvent.pointer({ keys: '[MouseLeft]' });
    await userEvent.pointer(
      [
        {
          target: sliderInput,
          coords: { x: 100, y: 10 },
          keys: '[MouseLeft>]',
        },
        {
          target: sliderInput,
          coords: { x: 200, y: 10 },
          keys: '[MouseLeft>]',
        },
        {
          target: sliderInput,
          coords: { x: 300, y: 10 },
          keys: '[MouseLeft>]',
        },
        {
          target: sliderInput,
          coords: { x: 400, y: 10 },
          keys: '[MouseLeft>]',
        },
        {
          target: sliderInput,
          coords: { x: 500, y: 10 },
          keys: '[MouseLeft>]',
        },

        { keys: '[/MouseLeft]' },
      ],
      { delay: 200 },
    );
    const submitButton = canvas.getAllByText('Strompreis berechnen');
    await userEvent.click(submitButton[0]);
  },
};

export const InteractiveGas: Story = {
  ...FullView,
  args: {
    ...FullView.args,
  },
  play: async ({ canvasElement }: { canvasElement: HTMLElement }) => {
    const canvas = within(canvasElement);
    const gasTabButton = canvas.getByRole('tab', { name: 'Gas' });
    await userEvent.click(gasTabButton);
  },
};

export const HalfViewInContainer: Story = {
  ...meta,
  render: (args: any) => ({
    components: { Calculator },
    setup() {
      return { args };
    },
    template: `
      <div class="container">
        <div class="row">
          <div class="col">
            <div class="row ce_row_start">
              <div class="col">
                <Calculator v-bind="args" />
              </div>
              <div class="col">
                <div style="background: white; padding: 32px; border-radius: 8px;">
                  <h2 style="margin-top: 0; margin-bottom: 16px; font-size: 24px; font-weight: bold;">Sample Text Block</h2>
                  <p style="margin-bottom: 12px; line-height: 1.6;">
                    This is a text block placed next to the calculator to simulate a CMS layout with two columns.
                  </p>
                  <p style="margin-bottom: 12px; line-height: 1.6;">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  </p>
                  <ul style="margin-bottom: 12px; padding-left: 24px;">
                    <li>Feature one</li>
                    <li>Feature two</li>
                    <li>Feature three</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  }),
  args: {
    ...meta.args,
    width: 'half',
    theme: 'dark',
    customerOptions: 'b2c_b2b',
    componentId: '123',
    parameters: JSON.stringify({
      customer: '01',
      postcode: '31785',
      city: 'Hameln',
      productrange: 'pes',
      calculator: '01',
      consumption: '2500',
      promotion_code: '0',
      agentCode: 0,
      reseller: 50,
    }),
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: false,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: true,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
  },
};

export const HalfView390px: Story = {
  ...meta,
  render: (args: any) => ({
    components: { Calculator },
    setup() {
      return { args };
    },
    template: `
      <div style="display: flex; gap: 24px; max-width: 1200px; margin: 0 auto; padding: 24px;">
        <div style="width: 390px; flex-shrink: 0;">
          <Calculator v-bind="args" />
        </div>
        <div style="flex: 1; background: white; padding: 32px; border-radius: 8px;">
          <h2 style="margin-top: 0; margin-bottom: 16px; font-size: 24px; font-weight: bold;">Adjacent Content</h2>
          <p style="margin-bottom: 12px; line-height: 1.6;">
            This demonstrates the calculator at exactly 390px width alongside other content, simulating a narrow column layout.
          </p>
          <p style="margin-bottom: 12px; line-height: 1.6;">
            The calculator maintains its half view styling while being constrained to a specific width.
          </p>
          <p style="margin-bottom: 12px; line-height: 1.6;">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
          </p>
          <ul style="margin-bottom: 12px; padding-left: 24px; line-height: 1.6;">
            <li>Responsive at 390px width</li>
            <li>Half view calculator</li>
            <li>Works with adjacent content</li>
          </ul>
        </div>
      </div>
    `,
  }),
  args: {
    ...meta.args,
    width: 'half',
    theme: 'dark',
    customerOptions: 'b2c_b2b',
    componentId: '456',
    parameters: JSON.stringify({
      customer: '01',
      postcode: '31785',
      city: 'Hameln',
      productrange: 'pes',
      calculator: '01',
      consumption: '2500',
      promotion_code: '0',
      agentCode: 0,
      reseller: 50,
    }),
    productTypes: {
      '01': {
        type: '01',
        typeReadable: 'power',
        isSelected: true,
        redirectPage: 'strom/preisberechnung',
        badge: 'Bis zu </br><b>250€</b>Bonus',
      },
      '02': {
        type: '02',
        typeReadable: 'gas',
        isSelected: false,
        redirectPage: 'gas/preisberechnung',
        badge: 'Bis zu </br><b>350€</b>Bonus',
      },
      '03': {
        type: '03',
        typeReadable: 'heatflow',
        isSelected: false,
        redirectPage: 'waermestrom/preisberechnung',
      },
    },
  },
};
